import * as THREE from 'three';

// ── Subsurface region-view cell tint ─────────────────────────────
//
// One transparent plane per highlighted cell, hugged just above the
// playmat surface and well below the card layer so cards (sites,
// burrowed units, submerged units) draw on top of it. The previous
// implementation used DOM overlays projected onto the canvas. Those
// always rendered above the entire WebGL scene, which buried cards
// under a tinted veil. Moving this into the 3D scene gives the
// correct layering: playmat → tint → cards.
//
// Each cell mesh is a 4-vertex BufferGeometry whose positions are
// rewritten every frame from the cell's projected world corners
// (cells aren't axis-aligned, and they can resize at runtime so a
// fixed PlaneGeometry won't do). UVs are baked once at create time
// so the fragment shader can compute a normalized distance-from-edge
// for the border + inner glow.
//
// Visual recipe (matches the prior CSS look. Soft tinted fill, an
// inset 1-2% border, an inset inner glow, a gentle 2.4s pulse):
//
//   alpha(uv) = fill   · 0.22
//             + border · 0.45 · smoothstep(0.05, 0, d)
//             + glow   · 0.10 · smoothstep(0.25, 0, d)
//
//   where d = min(uv.x, 1-uv.x, uv.y, 1-uv.y)
//
// Color is mixed from a darker fill tone to a brighter rim tone over
// the same edge-distance gradient. A subtle sin-pulse on overall
// alpha keeps the highlight breathing.
//
// renderOrder = -100 plus depthWrite = false guarantees:
//   - tint renders first in the transparent pass (lowest order)
//   - tint never blocks card depth-testing (no depth writes)
// so cards naturally draw over it without z-fighting.

// Clearly above the playmat surface but below the card layer. The
// table sits at Y=0.05, vanillaOverlay at Y=0.0501, uiOverlay at
// Y=0.06, cards at Y ≈ 0.075+. A Y of 0.07 puts the tint just below
// the card layer with ~1k units of depth-buffer margin against the
// table on 16-bit hardware. Well outside any z-fighting zone.
const TINT_Y = 0.07;

const COLORS = {
  water: {
    fill: new THREE.Color(0x50a0dc),
    edge: new THREE.Color(0x8cc8f0),
  },
  underground: {
    fill: new THREE.Color(0xa06428),
    edge: new THREE.Color(0xdcaa5a),
  },
};

const VERT = /* glsl */`
  varying vec2 vUv;
  void main() {
    vUv = uv;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

const FRAG = /* glsl */`
  uniform float uTime;
  uniform vec3  uFillColor;
  uniform vec3  uEdgeColor;
  // Per-edge "draw rim?" mask. Components are ordered top, right,
  // bottom, left (matching the UV layout tl(0,0) tr(1,0) br(1,1)
  // bl(0,1)). 1.0 = exposed (draw the bright rim), 0.0 = shared
  // with a same-kind cell (rim hidden so connected cells fuse into
  // one body with no internal divider).
  uniform vec4  uEdges;
  varying vec2  vUv;

  void main() {
    // Per-edge distances. Splits the previous min(dx,dy) into four
    // independent components so each edge can be selectively masked.
    float dTop    = vUv.y;
    float dRight  = 1.0 - vUv.x;
    float dBottom = 1.0 - vUv.y;
    float dLeft   = vUv.x;

    // Rim band. Narrow smoothstep along the very edge.
    float rimT = smoothstep(0.05, 0.0, dTop)    * uEdges.x;
    float rimR = smoothstep(0.05, 0.0, dRight)  * uEdges.y;
    float rimB = smoothstep(0.05, 0.0, dBottom) * uEdges.z;
    float rimL = smoothstep(0.05, 0.0, dLeft)   * uEdges.w;
    float border = max(max(rimT, rimR), max(rimB, rimL));

    // Inner glow. Wider falloff inward of the rim.
    float glowT = smoothstep(0.25, 0.0, dTop)    * uEdges.x;
    float glowR = smoothstep(0.25, 0.0, dRight)  * uEdges.y;
    float glowB = smoothstep(0.25, 0.0, dBottom) * uEdges.z;
    float glowL = smoothstep(0.25, 0.0, dLeft)   * uEdges.w;
    float glow = max(max(glowT, glowR), max(glowB, glowL));

    float pulse = 0.85 + 0.15 * sin(uTime * 2.6);

    // Subtle tint. Per-component breakdown:
    //   fill:   0.05 across the cell interior (very transparent.
    //           the colored hint barely tints the playmat)
    //   border: +0.22 at the very edge (kept punchy because the
    //           user explicitly likes the thicker, more opaque rim)
    //   glow:   +0.03 inward of the border (gentle inner halo)
    // Total alpha tops out at ~0.30 right at an exposed rim and
    // falls to ~0.05 at the cell center. Shared edges (uEdges
    // component = 0) contribute nothing. So two connected
    // same-kind cells fuse seamlessly without an internal seam.
    vec3  col   = mix(uFillColor, uEdgeColor, clamp(border * 0.75 + glow * 0.25, 0.0, 1.0));
    float alpha = (0.05 + border * 0.22 + glow * 0.03) * pulse;

    gl_FragColor = vec4(col, alpha);
  }
`;

/**
 * @param {'water' | 'underground'} kind
 */
export function createRegionTintMesh(kind) {
  const palette = COLORS[kind] || COLORS.water;

  const geometry = new THREE.BufferGeometry();
  // 4 vertices, positions populated by updateRegionTintGeometry.
  geometry.setAttribute('position', new THREE.BufferAttribute(new Float32Array(12), 3));
  // tl(0,0) → tr(1,0) → br(1,1) → bl(0,1). Same winding the helper
  // writes positions in, so the UVs line up with the cell corners.
  geometry.setAttribute('uv', new THREE.BufferAttribute(new Float32Array([
    0, 0,
    1, 0,
    1, 1,
    0, 1,
  ]), 2));
  geometry.setIndex([0, 1, 2, 0, 2, 3]);

  const material = new THREE.ShaderMaterial({
    uniforms: {
      uTime:      { value: 0 },
      uFillColor: { value: palette.fill },
      uEdgeColor: { value: palette.edge },
      // Per-edge rim mask. Defaults to all four edges exposed
      // (standalone cell). Updated each frame by
      // updateRegionTintEdges based on same-kind neighbour adjacency,
      // so two connected cells of the same kind drop the rim on
      // their shared edge and visually fuse into one body.
      uEdges:     { value: new THREE.Vector4(1, 1, 1, 1) },
    },
    vertexShader: VERT,
    fragmentShader: FRAG,
    transparent: true,
    depthWrite: false,
    side: THREE.DoubleSide,
    // tableMesh writes opaque depth with polygonOffset -1, which biases
    // its depth values forward in the buffer. The tint needs a stronger
    // negative bias to unambiguously win the depth comparison. Without
    // this, the tint can lose the depth test on 16-bit-depth hardware
    // even though it sits well above the table in world space, causing
    // the "invisible when static, flickers when the camera moves" symptom
    // of classic z-fighting between intentionally-coplanar surfaces.
    polygonOffset: true,
    polygonOffsetFactor: -2,
    polygonOffsetUnits: -2,
  });

  const mesh = new THREE.Mesh(geometry, material);
  // Threads between vanillaOverlayMesh (renderOrder 0.5) and uiOverlayMesh
  // (renderOrder 1), and well below cards (renderOrder = stack-rank index,
  // typically ≥ 0 and assigned per-frame by `_applyComputedY`). Negative
  // would make the tint render before the opaque-pass table, fail
  // depth-test against it, and disappear. This slot keeps it visible
  // on top of the playmat while cards still occlude it.
  mesh.renderOrder = 0.7;
  // Cells far from the camera-center can fall outside the frustum
  // sphere of a small 4-vertex geometry; skip the cull check to be
  // safe. We're never going to have more than ~50 of these.
  mesh.frustumCulled = false;
  mesh.userData = { kind };
  return mesh;
}

/**
 * Rewrite the four corner vertices to match the cell's current
 * world-space geometry. Cells get resized when the playmat layout
 * changes (e.g. spawn-config differences), so this needs to run
 * every frame, not just on create.
 *
 * `cellGeo` is the output of `sampleCellGeometry(grid, col, row)`:
 * an object with `tl`, `tr`, `br`, `bl` THREE.Vector3-ish points
 * (only x/z are used; the tint Y is fixed at TINT_Y).
 */
export function updateRegionTintGeometry(mesh, cellGeo) {
  const pos = mesh.geometry.attributes.position.array;
  pos[0]  = cellGeo.tl.x; pos[1]  = TINT_Y; pos[2]  = cellGeo.tl.z;
  pos[3]  = cellGeo.tr.x; pos[4]  = TINT_Y; pos[5]  = cellGeo.tr.z;
  pos[6]  = cellGeo.br.x; pos[7]  = TINT_Y; pos[8]  = cellGeo.br.z;
  pos[9]  = cellGeo.bl.x; pos[10] = TINT_Y; pos[11] = cellGeo.bl.z;
  mesh.geometry.attributes.position.needsUpdate = true;
  mesh.geometry.computeBoundingSphere();
}

/**
 * Set the per-edge rim mask for one cell. `edges` is `[top, right,
 * bottom, left]` of 0/1 numbers: 1 means the edge is exposed (draw
 * the bright rim), 0 means the edge is shared with a same-kind
 * neighbor (hide the rim so the two cells fuse seamlessly into one
 * body).
 *
 * Cheap to call every frame. Sets four uniform components on the
 * material. Three.js batches the GPU upload to once per frame.
 */
export function updateRegionTintEdges(mesh, edges) {
  const u = mesh.material.uniforms?.uEdges;
  if (!u) return;
  u.value.set(edges[0], edges[1], edges[2], edges[3]);
}

export function disposeRegionTintMesh(mesh) {
  mesh.geometry.dispose();
  mesh.material.dispose();
}
